import React from 'react';
import './LibraryFilterChips.css';

export type LibraryFilterChip = {
  key: string;
  /** What the chip says, e.g. "Location: Nashville". */
  label: string;
  onRemove: () => void;
};

/** The library's active filters as removable chips. Renders nothing when no filter is on. */
export default function LibraryFilterChips({
  chips,
  label = 'Active filters',
  emptyFocusRef,
}: {
  chips: LibraryFilterChip[];
  label?: string;
  /** Where focus goes when the last chip is removed, usually the Filters button. */
  emptyFocusRef?: React.RefObject<HTMLElement>;
}) {
  const listRef = React.useRef<HTMLUListElement>(null);
  const focusAfterRemove = React.useRef<number | null>(null);

  // Keyboard users keep their place: focus moves to the chip that took the removed one's
  // spot, or to emptyFocusRef once the list is gone.
  React.useEffect(() => {
    const index = focusAfterRemove.current;
    focusAfterRemove.current = null;
    if (index === null) return;
    const buttons = listRef.current?.querySelectorAll<HTMLButtonElement>('.library-filter-chip');
    if (buttons?.length) buttons[Math.min(index, buttons.length - 1)].focus();
    else emptyFocusRef?.current?.focus();
  }, [chips.length, emptyFocusRef]);

  if (chips.length === 0) return null;
  return (
    <ul ref={listRef} className="library-filter-chips" aria-label={label}>
      {chips.map((chip, index) => (
        <li key={chip.key} className="library-filter-chips__item">
          <button
            type="button"
            className="library-filter-chip"
            onClick={() => {
              focusAfterRemove.current = index;
              chip.onRemove();
            }}
            aria-label={`Remove filter ${chip.label}`}
          >
            <span className="library-filter-chip__label">{chip.label}</span>
            <svg className="library-filter-chip__icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </li>
      ))}
    </ul>
  );
}
